'use client';

import { useState, useEffect, useCallback } from 'react';
import { hasLeadSubmittedCookie, hasDismissedCookie, setDismissedCookie } from '@/lib/utils/lead-cookie';

interface UseExitIntentOptions {
  /** Distance from top of viewport to trigger (default: 50px) */
  threshold?: number;
  /** Minimum time on page before triggering (default: 5000ms) */
  delay?: number;
  /** Disable the hook entirely */
  disabled?: boolean;
  /** Session storage key to track if already shown */
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = 'exit-intent-shown';

/**
 * Hook to detect exit intent behavior.
 *
 * Desktop: Triggers when mouse moves toward top of viewport (browser chrome).
 * Mobile: Triggers when user rapidly scrolls up toward top of page.
 *
 * Only triggers once per session (uses sessionStorage).
 */
export function useExitIntent({
  threshold = 50,
  delay = 5000,
  disabled = false,
  storageKey = DEFAULT_STORAGE_KEY,
}: UseExitIntentOptions = {}): {
  showExitIntent: boolean;
  dismissExitIntent: () => void;
} {
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [canTrigger, setCanTrigger] = useState(false);

  // Check session storage, lead cookie, and set up delay timer
  useEffect(() => {
    if (disabled) return;

    // Check if already shown this session
    if (typeof window !== 'undefined') {
      const alreadyShown = sessionStorage.getItem(storageKey);
      if (alreadyShown === 'true') {
        setHasTriggered(true);
        return;
      }

      // Check if user submitted a lead form or dismissed the popup recently
      if (hasLeadSubmittedCookie() || hasDismissedCookie()) {
        setHasTriggered(true);
        return;
      }
    }

    // Wait for delay before allowing trigger
    const timer = setTimeout(() => {
      setCanTrigger(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [disabled, delay, storageKey]);

  // Desktop: Mouse leave detection
  useEffect(() => {
    if (disabled || hasTriggered || !canTrigger) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse is near top of viewport (heading to browser chrome/tabs)
      if (e.clientY < threshold) {
        // Check cookies again at trigger time (user may have submitted form mid-session)
        if (hasLeadSubmittedCookie() || hasDismissedCookie()) {
          setHasTriggered(true);
          return;
        }
        setShowExitIntent(true);
        setHasTriggered(true);
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(storageKey, 'true');
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [disabled, hasTriggered, canTrigger, threshold, storageKey]);

  // Mobile: Rapid scroll up detection
  useEffect(() => {
    if (disabled || hasTriggered || !canTrigger) return;

    let lastScrollY = window.scrollY;
    let scrollUpCount = 0;
    let scrollUpTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = lastScrollY - currentScrollY;

      // Detect rapid scroll up (user scrolling quickly toward top)
      if (scrollDelta > 50 && currentScrollY < 200) {
        scrollUpCount++;

        // Clear previous timeout
        if (scrollUpTimeout) clearTimeout(scrollUpTimeout);

        // Reset count after 500ms of no rapid scrolling
        scrollUpTimeout = setTimeout(() => {
          scrollUpCount = 0;
        }, 500);

        // Trigger after 3 rapid scroll-ups near top of page
        if (scrollUpCount >= 3) {
          // Check cookies again at trigger time (user may have submitted form mid-session)
          if (hasLeadSubmittedCookie() || hasDismissedCookie()) {
            setHasTriggered(true);
            return;
          }
          setShowExitIntent(true);
          setHasTriggered(true);
          if (typeof window !== 'undefined') {
            sessionStorage.setItem(storageKey, 'true');
          }
        }
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollUpTimeout) clearTimeout(scrollUpTimeout);
    };
  }, [disabled, hasTriggered, canTrigger, storageKey]);

  const dismissExitIntent = useCallback(() => {
    setShowExitIntent(false);
    setDismissedCookie();
  }, []);

  return { showExitIntent, dismissExitIntent };
}
